import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Interceptor which instructs Angular to not parse the JSON it receives and instead uses its own JSON parser.
 * Used to revive dates, which sounds a lot more dubious than it actually is.
 * @author: https://stackblitz.com/edit/angular-ivy-jkzamz
 */
@Injectable()
export class JsonParserHttpInterceptor implements HttpInterceptor {
    constructor(private readonly jsonParser: JsonParser) {}

    intercept(httpRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return httpRequest.responseType === 'json' ? this.handleJsonResponses(httpRequest, next) : next.handle(httpRequest);
    }

    private handleJsonResponses(httpRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next
            .handle(httpRequest.clone({ responseType: 'text' })) // Tells Angular to not process this request as JSON.
            .pipe(map(event => this.parseResponse(event))); // Use the custom parser instead.
    }

    private parseResponse(event: HttpEvent<any>): HttpEvent<any> {
        return event instanceof HttpResponse ? event.clone({ body: this.jsonParser.parse(event.body) }) : event;
    }
}

/** Custom JSON parser. */
@Injectable()
export abstract class JsonParser {
    abstract parse<T = any>(json: string): T;
}

/** JSON parser that can revive dates. */
@Injectable()
export class JsonDateParser implements JsonParser {
    parse<T = any>(json: string): T {
        if (!this.isValid(json)) return;

        return JSON.parse(json, jsonDateReviver);
    }

    isValid(json: string): boolean {
        if (json === '' || !json) return false;

        try {
            JSON.parse(json, jsonDateReviver);
            return true;
        } catch (error) {
            console.error('Invalid JSON => ', json);
            return false;
        }
    }
}

// Matching any ISO8601 string with timezone notation. Milliseconds optional. See tests for examples.
const dateISORegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:.\d+)?(?:Z|(\+|-)([\d|:]*))?$/;
const dateONLYRegex = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Returns a date object for any ISO8601 string with timezone notation. Milliseconds optional. */
export function jsonDateReviver(_key: string, value: string): any {
    if (typeof value === 'string' && dateISORegex.test(value)) {
        // Restituisce un oggetto Date con l'ora corretta in base al fuso orario di Roma, indipendentemente dal fuso orario specificato nella stringa.
        return new Date(moment.tz(value, 'Europe/Rome').format('YYYY-MM-DDTHH:mm:ssZ'));
    }

    if (typeof value === 'string' && dateONLYRegex.test(value)) {
        // Restituisce un oggetto Date con l'ora impostata a mezzanotte in base al fuso orario di Roma.
        return new Date(moment.tz(value, 'YYYY-MM-DD', 'Europe/Rome').format('YYYY-MM-DDTHH:mm:ss'));
    }

    return value;
}
